import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Bookmark, RotateCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '$lib/components/ui/Badge';
import { Button } from '$lib/components/ui/Button';
import { FilterChipStrip } from '$lib/components/table/FilterChipStrip';
import { UnitTable } from '$lib/components/table/UnitTable';
import { Pagination } from '$lib/components/table/Pagination';
import { RightClickHint } from '$lib/components/table/RightClickHint';
import { SaveSearchDialog } from '$lib/components/savedSearches/SaveSearchDialog';
import { useUnitsQueryState } from '$lib/hooks/useUnitsQueryState';
import { useHotkey } from '$lib/hooks/useHotkey';
import { buildListingsExportUrl, fetchUnits, isMockMode } from '$lib/api/client';
import { useSavedSearches } from '$lib/hooks/useSavedSearches';
import { formatDate } from '$lib/lib/formatters';
import { getIntlLocale } from '$lib/i18n';

const PAGE_SIZE_DEFAULT = 100;

export function UnitsPage() {
  const { query, set, hasFilters, filterCount, clear } = useUnitsQueryState();
  const navigate = useNavigate();
  const savedSearches = useSavedSearches();
  const [saveOpen, setSaveOpen] = useState(false);
  const saveButtonRef = useRef<HTMLButtonElement | null>(null);
  const { t } = useTranslation();
  const tag = getIntlLocale();

  const pageSize = query.pageSize ?? PAGE_SIZE_DEFAULT;
  const page = query.page ?? 1;

  const { data, isFetching, isPending, isError, error, refetch } = useQuery({
    queryKey: ['units', { ...query, page, pageSize }],
    queryFn: () => fetchUnits({ ...query, page, pageSize })
  });

  // Snap the page back into bounds when filters reduce the result set
  // below the current page window.
  useEffect(() => {
    if (!data) return;
    const pageCount = Math.max(1, Math.ceil(data.filtered / pageSize));
    if (page > pageCount) set({ page: undefined });
  }, [data, page, pageSize, set]);

  const openSave = useCallback(() => setSaveOpen(true), []);
  const closeSave = useCallback(() => {
    setSaveOpen(false);
    saveButtonRef.current?.focus();
  }, []);

  useHotkey({ key: 's', meta: true, ctrl: true, allowInInput: true }, (e) => {
    e.preventDefault();
    if (!saveOpen) openSave();
  });

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col">
      <header
        id="units-header"
        className="flex h-11 shrink-0 items-center gap-3 border-b border-rule bg-surface-2 px-3"
        data-testid="units-header"
      >
        <h1 className="text-sm font-semibold text-fg">{t('units.title')}</h1>
        {data && (
          <span
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className="text-xs text-fg-muted"
          >
            <span className="num text-fg">{data.filtered.toLocaleString(tag)}</span>
            {hasFilters && (
              <>
                {' '}
                {t('common.of')} <span className="num text-fg">{data.total.toLocaleString(tag)}</span>
              </>
            )}{' '}
            {t('units.unitsCount')}
          </span>
        )}
        {hasFilters && <Badge tone="accent">{t('units.filtersBadge', { count: filterCount })}</Badge>}
        {isFetching && !isError && <Badge tone="info">{t('units.loadingBadge')}</Badge>}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-2xs text-fg-subtle">{t('units.lastSync', { date: formatDate(data?.lastSync ?? null) })}</span>
          <Button
            ref={saveButtonRef}
            size="sm"
            variant="secondary"
            data-testid="save-filter"
            onClick={openSave}
            title={t('units.saveFilterTitle', { shortcut: isMac() ? '⌘ S' : 'Ctrl S' })}
            aria-keyshortcuts={isMac() ? 'Meta+S' : 'Control+S'}
          >
            <Bookmark size={12} />
            {t('units.saveFilter')}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            data-testid="export-excel"
            disabled={isMockMode()}
            title={
              isMockMode()
                ? t('units.exportExcelTitleMock')
                : t('units.exportExcelTitle')
            }
            onClick={() => {
              if (isMockMode()) return;
              const search = window.location.search;
              const norm = search.startsWith('?') ? search : `?${search}`;
              const match = savedSearches.find((s) => s.search === norm);
              window.open(
                buildListingsExportUrl(query, {
                  viewId: match?.id,
                  sourceUrl: window.location.href,
                }),
                '_blank',
                'noopener'
              );
            }}
          >
            {t('units.exportExcel')}
          </Button>
        </div>
      </header>

      <FilterChipStrip />
      <RightClickHint />

      {isError ? (
        <ErrorState message={(error as Error).message} onRetry={() => refetch()} />
      ) : !isPending && !isFetching && data && data.rows.length === 0 ? (
        <EmptyState hasFilters={hasFilters} onReset={clear} />
      ) : (
        <UnitTable
          rows={data?.rows ?? []}
          loading={isPending || isFetching}
          sort={query.sort}
          dir={query.dir}
          onSortChange={(sort, dir) => set({ sort, dir, page: undefined })}
          onRowOpen={(row) => {
            // Listing-centric pivot: row.id is a Listing UUID. Click
            // navigates to the proper /listings/:id detail page,
            // which collates everything we know about the listing
            // (lifecycle, conviction, snapshot history, BBR profile
            // when linked, portal link). Legacy fallback to
            // /units/:id is kept for any non-listing rows that
            // somehow flow through (defensive — currently never).
            if (row.url) {
              navigate(`/listings/${row.id}${window.location.search}`);
            } else {
              navigate(`/units/${row.id}${window.location.search}`);
            }
          }}
          onRowOpenInNewTab={(row) => {
            const path = row.url
              ? `/listings/${row.id}${window.location.search}`
              : `/units/${row.id}${window.location.search}`;
            window.open(path, '_blank', 'noopener');
          }}
        />
      )}

      <Pagination
        page={page}
        pageSize={pageSize}
        total={data?.filtered ?? 0}
        onPageChange={(next) => set({ page: next === 1 ? undefined : next })}
      />

      <SaveSearchDialog
        open={saveOpen}
        query={query}
        search={window.location.search}
        onClose={closeSave}
        onSaved={() => navigate('/saved')}
      />
    </div>
  );
}

function isMac(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Mac|iP(hone|ad|od)/.test(navigator.platform || navigator.userAgent || '');
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-1 items-center justify-center p-12" role="alert" data-testid="units-error">
      <div className="max-w-md text-center">
        <AlertTriangle className="mx-auto text-danger" size={28} />
        <h2 className="mt-3 text-md font-semibold text-fg">{t('units.errorTitle')}</h2>
        <p className="mt-1 text-xs text-fg-muted">{message || t('units.errorFallback')}</p>
        <Button variant="primary" size="sm" className="mt-4" onClick={onRetry}>
          <RotateCw size={12} />
          {t('common.retry')}
        </Button>
      </div>
    </div>
  );
}

function EmptyState({ hasFilters, onReset }: { hasFilters: boolean; onReset: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-1 items-center justify-center p-12" data-testid="units-empty">
      <div className="max-w-md text-center">
        <h2 className="text-md font-semibold text-fg">
          {hasFilters ? t('units.emptyNoMatchTitle') : t('units.emptyTitle')}
        </h2>
        <p className="mt-1 text-xs text-fg-muted">
          {hasFilters ? t('units.emptyNoMatchBody') : t('units.emptyBody')}
        </p>
        {hasFilters && (
          <Button variant="primary" size="sm" className="mt-4" onClick={onReset} data-testid="empty-reset">
            {t('units.resetAllFilters')}
          </Button>
        )}
      </div>
    </div>
  );
}
